import { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, ArrowLeft, Paperclip, X, FileText, WifiOff, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import useChatSync from "@/hooks/useChatSync";
import MessageBubble, { attachmentPreviewLabel } from "@/components/chat/MessageBubble";
import ChatDateSeparator from "@/components/chat/ChatDateSeparator";
import { buildChatTimeline, formatConversationPreviewTime } from "@/lib/chatTimeline";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function ChatPanel({ mode = "coach" }) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const withUserId = searchParams.get("with");
  usePushNotifications(!!user);

  const [selectedUser, setSelectedUser] = useState(null);
  const [newMsg, setNewMsg] = useState("");
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [attachmentMeta, setAttachmentMeta] = useState(null);
  const [attachmentIsPdf, setAttachmentIsPdf] = useState(false);
  const [attachmentName, setAttachmentName] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [newMessagesBelow, setNewMessagesBelow] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const scrollRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const prevMessageCountRef = useRef(0);
  const prevTimelineLengthRef = useRef(0);
  const prevScrollHeightRef = useRef(0);
  const shouldPreserveScrollRef = useRef(false);
  const loadingOlderRef = useRef(false);
  const exhaustedOlderRef = useRef(false);

  const handleSelectFromDeepLink = useCallback((match) => {
    setSelectedUser(match);
    setMobileShowChat(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("with");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const {
    conversations,
    messages,
    otherTyping,
    connectionState,
    isOnline,
    sendMessage,
    uploadAttachment,
    signalTyping,
    preloadConversation,
    loadOlderMessages,
    createClientMessageId,
  } = useChatSync({
    mode,
    selectedUserId: selectedUser?.user_id,
    withUserId,
    onSelectUser: handleSelectFromDeepLink,
  });

  const timeline = useMemo(() => buildChatTimeline(messages), [messages]);

  useEffect(() => {
    exhaustedOlderRef.current = false;
    prevTimelineLengthRef.current = 0;
    isAtBottomRef.current = true;
    setIsAtBottom(true);
  }, [selectedUser?.user_id]);

  useEffect(() => {
    if (mode === "student" && conversations[0] && !selectedUser && !withUserId) {
      setSelectedUser(conversations[0]);
      setMobileShowChat(true);
    }
  }, [mode, conversations, selectedUser, withUserId]);

  useEffect(() => {
    const count = messages.length;
    if (count > prevMessageCountRef.current && !isAtBottom) {
      setNewMessagesBelow((n) => n + (count - prevMessageCountRef.current));
    } else if (isAtBottom) {
      setNewMessagesBelow(0);
    }
    prevMessageCountRef.current = count;
  }, [messages.length, isAtBottom]);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom < 80;
    isAtBottomRef.current = atBottom;
    setIsAtBottom(atBottom);
  }, []);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    isAtBottomRef.current = true;
    setIsAtBottom(true);
    setNewMessagesBelow(0);
  }, []);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    if (shouldPreserveScrollRef.current) {
      const delta = el.scrollHeight - prevScrollHeightRef.current;
      el.scrollTop += delta;
      shouldPreserveScrollRef.current = false;
      updateScrollState();
      return;
    }

    const grew = timeline.length > prevTimelineLengthRef.current;
    prevTimelineLengthRef.current = timeline.length;

    if (grew && isAtBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [timeline, updateScrollState]);

  const handleScroll = useCallback(async () => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();

    if (
      el.scrollTop < 120
      && selectedUser
      && !loadingOlderRef.current
      && !exhaustedOlderRef.current
      && messages.length > 0
    ) {
      loadingOlderRef.current = true;
      setLoadingOlder(true);
      prevScrollHeightRef.current = el.scrollHeight;

      const added = await loadOlderMessages(selectedUser.user_id);

      if (added) {
        shouldPreserveScrollRef.current = true;
      } else {
        exhaustedOlderRef.current = true;
      }

      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [selectedUser, messages.length, loadOlderMessages, updateScrollState]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";

    if (!isImage && !isPdf) {
      toast.error("Seules les images et les PDF sont acceptés");
      return;
    }
    if (isImage && file.size > 5 * 1024 * 1024) {
      toast.error("Image max 5 Mo");
      return;
    }
    if (isPdf && file.size > 10 * 1024 * 1024) {
      toast.error("PDF max 10 Mo");
      return;
    }

    setPendingFile(file);
    setAttachmentIsPdf(isPdf);
    setAttachmentName(file.name);
    if (isImage) {
      const reader = new FileReader();
      reader.onload = () => setAttachmentPreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview(null);
    }
    e.target.value = "";
  };

  const clearAttachment = () => {
    setAttachmentPreview(null);
    setAttachmentMeta(null);
    setAttachmentIsPdf(false);
    setAttachmentName("");
    setPendingFile(null);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if ((!newMsg.trim() && !pendingFile) || !selectedUser || sending) return;

    setSending(true);
    const clientMessageId = createClientMessageId();
    const content = newMsg.trim();
    let attachment = attachmentMeta;

    const optimisticMessage = {
      message_id: clientMessageId,
      client_message_id: clientMessageId,
      sender_id: user?.user_id,
      receiver_id: selectedUser.user_id,
      content: content || (attachmentIsPdf ? "📎 PDF" : pendingFile ? "📷 Photo" : ""),
      attachment_url: attachment?.attachment_url || attachmentPreview || null,
      attachment_thumb_url: attachment?.attachment_thumb_url || null,
      attachment_type: attachment?.attachment_type || (attachmentIsPdf ? "application/pdf" : pendingFile?.type),
      delivery_status: "sending",
      is_read: false,
      created_at: new Date().toISOString(),
    };

    try {
      if (pendingFile && !attachment) {
        attachment = await uploadAttachment(pendingFile);
        setAttachmentMeta(attachment);
      }

      const result = await sendMessage({
        receiverId: selectedUser.user_id,
        content,
        attachmentUrl: attachment?.attachment_url,
        attachmentThumbUrl: attachment?.attachment_thumb_url,
        attachmentType: attachment?.attachment_type,
        optimisticMessage,
      });

      setNewMsg("");
      clearAttachment();

      if (result.queued) {
        toast.message("Hors ligne — message en file d'attente");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  const handleInputChange = (e) => {
    setNewMsg(e.target.value);
    if (selectedUser && e.target.value.trim()) {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        const now = Date.now();
        if (now - lastTypingSentRef.current < 2000) return;
        lastTypingSentRef.current = now;
        signalTyping(selectedUser.user_id, true);
      }, 300);
    } else if (selectedUser) {
      signalTyping(selectedUser.user_id, false);
    }
  };

  const selectConversation = (c) => {
    setSelectedUser(c);
    setMobileShowChat(true);
    setNewMessagesBelow(0);
    isAtBottomRef.current = true;
    setIsAtBottom(true);
    prevTimelineLengthRef.current = 0;
    requestAnimationFrame(() => scrollToBottom(false));
  };

  if (mode === "student" && conversations.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-400">Aucun coach assigné pour le moment.</p>
      </div>
    );
  }

  const showList = !mobileShowChat;
  const showChat = mobileShowChat || (mode === "coach" && selectedUser);
  const apiOrigin = typeof window !== "undefined" ? window.location.origin : API_BASE;

  return (
    <div className="w-full max-w-full min-w-0 grid grid-cols-1 lg:grid-cols-[minmax(240px,280px)_minmax(0,1fr)] gap-4 h-full min-h-[420px]">
      <div className={`min-w-0 w-full bg-white rounded-2xl border border-slate-100 overflow-hidden flex flex-col ${showList ? "flex" : "hidden lg:flex"}`}>
        <div className="p-4 border-b border-slate-100 flex items-center justify-between shrink-0">
          <p className="text-sm font-medium text-slate-500">Conversations</p>
          {(!isOnline || connectionState === "offline") && (
            <span className="text-[10px] text-amber-600 flex items-center gap-1 shrink-0">
              <WifiOff className="w-3 h-3" /> Hors ligne
            </span>
          )}
        </div>
        <ScrollArea className="flex-1 min-h-0">
          {conversations.length === 0 ? (
            <p className="text-sm text-slate-400 p-4 text-center">Aucune conversation</p>
          ) : (
            conversations.map((c) => (
              <button
                key={c.user_id}
                type="button"
                onClick={() => selectConversation(c)}
                onMouseEnter={() => preloadConversation(c.user_id)}
                onFocus={() => preloadConversation(c.user_id)}
                className={`w-full min-w-0 flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left border-b border-slate-50 ${selectedUser?.user_id === c.user_id ? "bg-blue-50" : ""}`}
              >
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarImage src={c.picture ? `${apiOrigin}${c.picture}` : undefined} />
                  <AvatarFallback className="bg-blue-100 text-blue-600 text-sm font-semibold">{c.name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-slate-900 truncate">{c.name}</p>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {c.last_message?.created_at && (
                        <span className="text-[10px] text-slate-400 tabular-nums">
                          {formatConversationPreviewTime(c.last_message.created_at)}
                        </span>
                      )}
                      {c.unread_count > 0 && (
                        <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center">{c.unread_count}</span>
                      )}
                    </div>
                  </div>
                  {c.last_message && (
                    <p className="text-xs text-slate-400 truncate mt-0.5">
                      {attachmentPreviewLabel(c.last_message)}
                    </p>
                  )}
                </div>
              </button>
            ))
          )}
        </ScrollArea>
      </div>

      <div className={`min-w-0 w-full bg-white rounded-2xl border border-slate-100 flex flex-col overflow-hidden relative ${showChat ? "flex" : "hidden lg:flex"}`}>
        {!selectedUser ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm px-4 text-center">
            Sélectionnez une conversation
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-slate-100 flex items-center gap-3 shrink-0 min-w-0">
              <Button variant="ghost" size="icon" className="lg:hidden shrink-0" onClick={() => setMobileShowChat(false)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <Avatar className="h-9 w-9 shrink-0">
                <AvatarImage src={selectedUser.picture ? `${apiOrigin}${selectedUser.picture}` : undefined} />
                <AvatarFallback className="bg-blue-100 text-blue-600 text-sm font-semibold">{selectedUser.name?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className="font-medium text-slate-900 truncate">{selectedUser.name}</p>
                {otherTyping ? (
                  <p className="text-xs text-blue-500 animate-pulse truncate">{selectedUser.name} est en train d'écrire...</p>
                ) : connectionState === "connected" ? (
                  <p className="text-xs text-emerald-500">En ligne</p>
                ) : null}
              </div>
            </div>

            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden overscroll-y-contain touch-pan-y bg-slate-50/40 px-3 sm:px-4"
            >
              {loadingOlder && (
                <p className="text-center text-xs text-slate-400 py-3 sticky top-0 bg-slate-50/90 z-[1]">
                  Chargement des messages précédents…
                </p>
              )}
              <div className="flex flex-col min-h-full py-2">
                {timeline.map((item, index) => (
                  <div key={item.id} className={`w-full min-w-0 shrink-0 ${index === 0 ? "mt-auto" : ""}`}>
                    {item.type === "date" ? (
                      <ChatDateSeparator label={item.label} />
                    ) : (
                      <div className="pb-1">
                        <MessageBubble message={item.message} isMine={item.message.sender_id === user?.user_id} />
                      </div>
                    )}
                  </div>
                ))}
                {otherTyping && (
                  <p className="text-xs text-blue-500 animate-pulse px-1 pb-2">{selectedUser.name} écrit...</p>
                )}
              </div>
            </div>

            {newMessagesBelow > 0 && (
              <button
                type="button"
                onClick={() => scrollToBottom(true)}
                className="absolute bottom-[4.5rem] left-1/2 -translate-x-1/2 z-10 bg-blue-600 text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1 hover:bg-blue-700"
              >
                <ChevronDown className="w-3.5 h-3.5" />
                {newMessagesBelow} nouveau{newMessagesBelow > 1 ? "x" : ""} message{newMessagesBelow > 1 ? "s" : ""}
              </button>
            )}

            {(attachmentPreview || attachmentIsPdf) && (
              <div className="px-4 py-2 border-t border-slate-100 flex items-center gap-2 min-w-0 shrink-0 bg-white">
                {attachmentIsPdf ? (
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileText className="w-5 h-5 text-red-500 shrink-0" />
                    <span className="text-xs text-slate-500 truncate">{attachmentName || "document.pdf"}</span>
                  </div>
                ) : (
                  <img src={attachmentPreview} alt="Aperçu" className="h-14 w-14 rounded-lg object-cover shrink-0" />
                )}
                <span className="text-xs text-slate-500 flex-1 min-w-0 truncate">
                  {attachmentIsPdf ? "PDF prêt à envoyer" : "Image prête à envoyer"}
                </span>
                <button type="button" onClick={clearAttachment} className="text-slate-400 hover:text-slate-600 shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <form onSubmit={handleSend} className="p-3 sm:p-4 border-t border-slate-100 flex gap-2 min-w-0 shrink-0 bg-white">
              <input type="file" accept="image/*,application/pdf" className="hidden" id="chat-file-input" onChange={handleFileSelect} />
              <Button type="button" variant="outline" size="icon" className="shrink-0 h-11 w-11" onClick={() => document.getElementById("chat-file-input")?.click()}>
                <Paperclip className="w-4 h-4" />
              </Button>
              <Input
                value={newMsg}
                onChange={handleInputChange}
                placeholder="Votre message..."
                className="h-11 flex-1 min-w-0"
              />
              <Button
                type="submit"
                disabled={sending || (!newMsg.trim() && !pendingFile)}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11 px-5 sm:px-6 shrink-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}